import React, { useState, useRef, useEffect } from "react";
import "./index.scss";
import { StatusManagementProps } from "./type";
import {
  GraphView, // required
  INode,
  IEdge,
  GraphUtils,
} from "react-digraph";
import GraphConfig, {
  NODE_KEY,
  normalizeEdge,
  normalizeTitleNode,
} from "./helpers/GraphConfig";
import api from "api";
import { connect } from "react-redux";
import ModalStatus from "./components/ModalStatus";
import { Popover } from "reactstrap";
import t from "utils/Lang";
import { Select } from "shared";

const StatusManagement = ({ user }: StatusManagementProps) => {
  const contentTypeOptions = [
    { value: "site", label: "Site" },
    { value: "store", label: "Store" },
  ];
  const graphRef = useRef<any>();
  const [nodes, setNodes] = useState<Array<any>>([]);
  const [edges, setEdges] = useState<Array<any>>([]);
  const [selected, setSelected] = useState<any>(null);
  const [currentStatus, setCurrentStatus] = useState<any>(null);
  const [isOpenModalStatus, setOpenModalStatus] = useState(false);
  const [currentCoordinate, setCurrentCoordinate] = useState<any>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [listRoles, setListRoles] = useState([]);
  const [statusData, setStatusData] = useState({
    isOpen: false,
    statusRoles: [],
  });
  const [selectedType, setSelectedType] = useState(contentTypeOptions[0]);

  useEffect(() => {
    fetchData();
    fetchListRole();
  }, [selectedType.value]);

  const fetchListRole = async () => {
    const res = await api.getListRole();
    if (res.status == "ok") {
      setListRoles(res.result);
    }
  };

  const togglePopover = () => {
    setPopoverOpen(!popoverOpen);
  };

  const toggleModalStatus = async () => {
    if (!statusData.isOpen) {
      setSelected(null);
    }
    let statusRoles: any = [];
    if (selected) {
      const statusRoleRes = await api.getStatusRole(selected.id);
      if (statusRoleRes.status == "ok") {
        statusRoles = statusRoleRes.data.result?.map?.((role: any) => ({
          value: role.role_id,
        }));
      }
    }
    setStatusData((statusData) => ({
      isOpen: !statusData.isOpen,
      statusRoles,
    }));
  };

  const fetchData = async () => {
    const res = await api.getExistedStatus(selectedType.value);
    const { edges, nodes } = res.data;
    setNodes(nodes);
    setEdges(normalizeEdge(edges));
  };

  const onSelectNode = (evt: any) => {
    setSelected(evt);
    setCurrentStatus(evt);
  };
  const onCreateNode = (x: number, y: number, mouseEvt: any) => {
    toggleModalStatus();
    setCurrentCoordinate({ x, y });
  };
  const onUpdateNode = (evt: any) => {
    const { id, x, y } = evt;
    api.updateCoordinatorStatus({ x, y }, id);
  };
  const onDeleteNode = (evt: any) => {
    setNodes((nodes) => nodes.filter((n) => n.id != evt.id));
  };
  const onSelectEdge = (evt: any) => {
    setSelected(evt);
  };
  const onCreateEdge = async (sourceNode: INode, targetNode: INode) => {
    const isExisted = !!edges.find(
      (ed: any) => ed.source == sourceNode.id && ed.target == targetNode.id
    );
    if (!isExisted) {
      const newEdges = [
        {
          source: sourceNode.id,
          target: targetNode.id,
          type: "emptyEdge",
          isBackWard: !!edges.find(
            (ed: any) =>
              ed.target == sourceNode.id && ed.source == targetNode.id
          ),
        },
      ];
      const res = await api.createEdge({
        source: sourceNode.id,
        target: targetNode.id,
      });
      if (res.status == "ok") {
        setEdges((edges: any) => {
          return [...edges, ...newEdges];
        });
      }
    }
  };
  const onSwapEdge = (sourceNode: INode, targetNode: INode, edge: IEdge) => {
    const newEdge = {
      source: sourceNode.id,
      target: targetNode.id,
      type: "emptyEdge",
    };
    setEdges((edges: any) => {
      return [
        ...edges.filter(
          (ed: any) => ed.source != edge.source && ed.target != edge.target
        ),
        newEdge,
      ];
    });
  };
  const onDeleteEdge = async (evt: any) => {
    const res = await api.deleteEdge({
      source: evt.source,
      target: evt.target,
    });
    if (res.status == "ok") {
      setEdges((edges: any) => {
        const newEdge = edges.filter(
          (ed: any) => !(ed.source == evt.source && ed.target == evt.target)
        );
        return newEdge;
      });
    }
  };

  const renderNodeText = (data: any, id: any, isSelected: boolean) => {
    const className = GraphUtils.classNames("node-text", {
      selected: isSelected,
    });
    return (
      <text className={className} textAnchor="middle">
        {normalizeTitleNode(data.title)}
      </text>
    );
  };

  const onUpdateStatus = (status: any) => {
    setNodes((nodes) => {
      if (!status.is_active) return nodes.filter((n) => n.id != status.id);
      return nodes.map((n) => {
        if (n.id == status.id) return { ...n, title: status.name };
        return n;
      });
    });
  };
  const onCreateStatus = (status: any) => {
    setNodes((nodes) => {
      const newNodes = [
        ...nodes,
        {
          id: status.id,
          title: status.name,
          x: status.x,
          y: status.y,
        },
      ];
      return newNodes;
    });
    setCurrentCoordinate(null);
  };

  return (
    <div id="graph" className="status-management__container">
      <GraphView
        maxTitleChars={100}
        ref={graphRef}
        nodeKey={NODE_KEY}
        nodes={nodes}
        edges={edges}
        selected={selected}
        nodeTypes={GraphConfig.NodeTypes}
        nodeSubtypes={GraphConfig.NodeSubtypes}
        edgeTypes={GraphConfig.EdgeTypes}
        onSelectNode={onSelectNode}
        onCreateNode={onCreateNode}
        onUpdateNode={onUpdateNode}
        onDeleteNode={onDeleteNode}
        onSelectEdge={onSelectEdge}
        onCreateEdge={onCreateEdge}
        onSwapEdge={onSwapEdge}
        onDeleteEdge={onDeleteEdge}
        renderNodeText={renderNodeText}
      />
      <div className="status-management__actions-left">
        <Select
          data={contentTypeOptions}
          label="Content type"
          values={[selectedType]}
          onChanged={(selectedOptions) => setSelectedType(selectedOptions?.[0])}
          // onItemChanged={(item, isSelected) =>
          //   onItemChanged?.(attributeName, item, isSelected)
          // }
        />
      </div>
      <div className="status-management__actions">
        {selected?.id && (
          <button
            className="status-management__button"
            onClick={toggleModalStatus}
          >
            {t().action__edit}
          </button>
        )}
        <button
          id="button-hint"
          className="status-management__button"
          onClick={togglePopover}
        >
          {t().guide_line}
        </button>
      </div>
      <ModalStatus
        isOpen={statusData.isOpen}
        toggle={toggleModalStatus}
        user={user}
        id={currentStatus?.id}
        onClosed={() => setCurrentStatus(null)}
        onUpdated={onUpdateStatus}
        onCreated={onCreateStatus}
        coordinate={currentCoordinate}
        listRoles={listRoles}
        statusRoles={statusData.statusRoles}
        selectedType={selectedType}
      />
      <Popover
        placement="bottom"
        isOpen={popoverOpen}
        target="button-hint"
        toggle={togglePopover}
      >
        <div style={{ display: "flex", flexDirection: "column", padding: 10 }}>
          <span>{t().setting_status__guide_create}</span>
          <br />
          <span>{t().setting_status__guide_edit}</span>
          <br />
          <span>{t().setting_status__guide_link}</span>
        </div>
      </Popover>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
  };
};

export default connect(mapStateToProps)(StatusManagement);
